import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { SearchSelect } from '../../components/SearchSelect';
import type { Bom, BomLine, BomOperation, Item, Operation } from '../../lib/types';

export function BomInputsEditor({ bom, onSaved }: { bom: Bom; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<BomLine[]>([]);
  const [ops, setOps] = useState<BomOperation[]>([]);
  const [quantity, setQuantity] = useState(String(bom.quantity));
  const [notes, setNotes] = useState((bom.notes || '').replace(/\s*\|?\s*\[no_material_required\].*$/i, ''));
  const [laborOnly, setLaborOnly] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setQuantity(String(bom.quantity));
    setNotes((bom.notes || '').replace(/\s*\|?\s*\[no_material_required\].*$/i, ''));
  }, [bom.id, bom.updated_at, bom.quantity, bom.notes]);

  async function copyVersion() {
    setBusy(true); setError('');
    try {
      const result = await api.post<{ data: Bom }>(`/api/boms/${bom.id}/revisions`, {});
      window.location.assign(`/boms?open=${encodeURIComponent(result.data.id)}`);
    } catch (e) { setError(e instanceof ApiError ? e.message : '复制失败'); setBusy(false); }
  }

  function edit() {
    setLines((bom.lines ?? []).map(l => ({ ...l })));
    setOps((bom.operations ?? []).map(o => ({ ...o })));
    setLaborOnly(!(bom.lines?.length)); setReason(''); setError(''); setOpen(true);
  }

  async function save() {
    setBusy(true); setError('');
    try {
      // 路径必须是 PUT /api/boms/:id（2026-10-29 修复）。
      //
      // 原写作 `/api/boms/${id}/production-inputs`，而后端只注册了 `PUT /api/boms/:id`
      // （app.js 的 bomIdMatch）。多出的 /production-inputs 不匹配任何路由，
      // 请求落到 404，表现为「点保存没反应 / 保存失败」。
      // 后端 updateDraftBomInputs() 接受的字段与下面的 payload 完全一致，改路径即可。
      await api.put(`/api/boms/${bom.id}`, {
        item_id: bom.item_id, quantity: Number(quantity), notes,
        expected_updated_at: bom.updated_at,
        lines: laborOnly ? [] : lines.map(l => ({ item_id: l.item_id, quantity: Number(l.quantity), uom: l.uom, operation_name: l.operation_name || null })),
        operations: ops.map(o => ({ operation_id: o.operation_id, workstation: o.workstation, time_minutes: Number(o.time_minutes), hourly_cost: Number(o.hourly_cost), description: o.description || null })),
        no_material_required: laborOnly, no_material_reason: reason,
      });
      setOpen(false); onSaved();
    } catch (e) { setError(e instanceof ApiError ? e.message : '保存失败'); }
    finally { setBusy(false); }
  }

  if (bom.status !== 'draft') return <div className="panel-body">
    <button className="btn primary" disabled={busy} onClick={copyVersion}>{busy ? '创建新版中…' : '复制为新版并编辑工艺'}</button>
    {error ? <p role="alert" className="notice bad">{error}</p> : null}
    <p className="muted">版本 {bom.version} 保持不变。新版本从最高版本号递增，保留版本来源关系，改完后须重新审核批准。</p>
  </div>;

  if (!open) return <div className="panel-body">
    <button className="btn primary" onClick={edit}>编辑 BOM 内容</button>
    <p className="muted">草稿可修改用料、工序、工时和说明；任何内容变更都会重置工序质量审核状态。</p>
  </div>;

  return <section className="panel-body">
    {error ? <p role="alert" className="notice bad">{error}</p> : null}
    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
      <label>成品标准数量<input aria-label="成品标准数量" type="number" min="0.001" step="any" value={quantity} onChange={e => setQuantity(e.target.value)} /></label>
      <label>备注<input aria-label="BOM备注" value={notes} onChange={e => setNotes(e.target.value)} /></label>
    </div>
    <h4>用料明细</h4>
    <label><input type="checkbox" checked={laborOnly} onChange={e => setLaborOnly(e.target.checked)} disabled={busy} /> 本 BOM 无计划材料，仅记录加工工序</label>
    {laborOnly ? <input aria-label="无材料原因" placeholder="说明无计划材料原因（必填）" value={reason} onChange={e => setReason(e.target.value)} /> : <>
      {lines.map((line, index) => <div key={line.id || index} className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.2fr auto', gap: 8, margin: '8px 0', alignItems: 'end' }}>
        <SearchSelect<Item> endpoint="/api/items" value={line.item_id} label={`${line.item_code ?? ''} ${line.item_name_zh ?? ''}`.trim()} placeholder="选择材料" optionTitle={i => `${i.code} · ${i.name_zh}`} onChange={item => setLines(ls => ls.map((l, j) => j === index ? { ...l, item_id: item?.id ?? '', item_code: item?.code, item_name_zh: item?.name_zh } : l))} />
        <label>用量<input aria-label={`用量 ${index + 1}`} type="number" min="0.000001" step="any" value={line.quantity} onChange={e => setLines(ls => ls.map((l, j) => j === index ? { ...l, quantity: Number(e.target.value) } : l))} /></label>
        <label>单位<input aria-label={`单位 ${index + 1}`} value={line.uom} onChange={e => setLines(ls => ls.map((l, j) => j === index ? { ...l, uom: e.target.value } : l))} /></label>
        <label>适用工序<input aria-label={`适用工序 ${index + 1}`} value={line.operation_name || ''} onChange={e => setLines(ls => ls.map((l, j) => j === index ? { ...l, operation_name: e.target.value } : l))} /></label>
        <button className="btn" disabled={busy} onClick={() => setLines(ls => ls.filter((_, j) => j !== index))}>删除</button>
      </div>)}
      <button className="btn" disabled={busy} onClick={() => setLines(ls => [...ls, { id: '', bom_id: bom.id, item_id: '', quantity: 1, uom: 'Unidad' }])}>+ 添加用料</button>
    </>}

    <h4 style={{ marginTop: 16 }}>生产工序</h4>
    {ops.map((op, index) => <div key={op.id || index} className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.2fr auto', gap: 8, margin: '8px 0', alignItems: 'end' }}>
      <SearchSelect<Operation> endpoint="/api/operations" value={op.operation_id} label={`${op.operation_code ?? ''} ${op.operation_name ?? ''}`.trim()} placeholder="选择工序" optionTitle={o => `${o.code} · ${o.name_zh}`} onChange={operation => setOps(os => os.map((o, j) => j === index ? { ...o, operation_id: operation?.id ?? '', operation_code: operation?.code, operation_name: operation?.name_zh, workstation: operation?.workstation || '', time_minutes: operation?.default_minutes ?? 0, hourly_cost: operation?.hourly_cost ?? 0 } : o))} />
      <label>工位<input aria-label={`工位 ${index + 1}`} value={op.workstation || ''} onChange={e => setOps(os => os.map((o, j) => j === index ? { ...o, workstation: e.target.value } : o))} /></label>
      <label>工时（分钟）<input aria-label={`工时 ${index + 1}`} type="number" min="0" step="any" value={op.time_minutes} onChange={e => setOps(os => os.map((o, j) => j === index ? { ...o, time_minutes: Number(e.target.value) } : o))} /></label>
      <label>工序说明<input aria-label={`工序说明 ${index + 1}`} value={op.description || ''} onChange={e => setOps(os => os.map((o, j) => j === index ? { ...o, description: e.target.value } : o))} /></label>
      <button className="btn" disabled={busy} onClick={() => setOps(os => os.filter((_, j) => j !== index))}>删除</button>
    </div>)}
    <button className="btn" disabled={busy} onClick={() => setOps(os => [...os, { id: '', bom_id: bom.id, operation_id: '', time_minutes: 0, hourly_cost: 0, qc_required: 0, sort_order: os.length }])}>+ 添加工序</button>
    <p className="muted">工序修改后质量审核会恢复为“待审核”，需重新完成审核才能批准 BOM。</p>
    <div className="detail-actions">
      <button className="btn primary" disabled={busy || !Number(quantity) || (laborOnly && !reason.trim())} onClick={save}>{busy ? '保存中…' : '保存 BOM 修改'}</button>
      <button className="btn" disabled={busy} onClick={() => setOpen(false)}>取消</button>
    </div>
  </section>;
}
